/**
 * /invite?token=... — the page an emailed invite link opens. Previews the
 * invite (public, doesn't consume the token — see invite.service.ts#previewInvite
 * on the backend) then branches into exactly the two accept paths the
 * backend supports, matching the security invariant from Phase 2: a
 * brand-new identity sets its own password here; an EXISTING account can
 * only accept by actually logging in (own real credentials), never by
 * setting a fresh password through this link.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Users, XCircle, CheckCircle2 } from 'lucide-react';
import { ThemeCtx, useTheme, useThemeState, THEMES, NavBar, Footer } from '../home/marketing-shell';
import { useAuth } from '@/hooks/useAuth';
import PasswordField from '@/components/auth/PasswordField';
import { previewInvite, type InvitePreview } from '@/services/invite.api';
import { roleLabel } from '@/utils/roleLabel.util';

type LoadState = 'loading' | 'ready' | 'invalid';

function InviteCardShell({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme();
  const t = THEMES[theme];
  return (
    <div style={{
      background: t.card, border: `1px solid ${t.border}`, borderRadius: 24, padding: '40px 32px',
      display: 'flex', flexDirection: 'column', gap: 18, width: '100%', maxWidth: 460,
    }}>
      {children}
    </div>
  );
}

function InvalidInviteCard({ message }: { message: string }) {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const navigate = useNavigate();
  return (
    <InviteCardShell>
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <XCircle size={26} color="#ef4444" />
        </div>
        <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 22, color: t.fg }}>
          Invitation not valid
        </h1>
        <p style={{ color: t.mutedFg, fontSize: 14.5, lineHeight: 1.6 }}>{message}</p>
        <button
          onClick={() => navigate('/')}
          style={{
            marginTop: 4, padding: '11px 20px', borderRadius: 10, border: `1px solid ${t.border}`,
            background: 'transparent', color: t.fg, fontWeight: 600, fontSize: 13.5, cursor: 'pointer',
          }}
        >
          Go to homepage
        </button>
      </div>
    </InviteCardShell>
  );
}

/** Existing-account branch: not authenticated (or logged in as a different
 *  email) → send through /login with a redirect back here. Authenticated as
 *  the invited email already → one-click confirm via acceptInviteAsExistingUser. */
function ExistingAccountCard({ token, preview }: { token: string; preview: InvitePreview }) {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, acceptInviteAsExistingUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState<{ name: string } | null>(null);
  // Distinct from `error`: this identity can never accept this invite (one
  // email = one tenant, ever — see invite.service.ts's module comment), as
  // opposed to a transient failure worth retrying.
  const [blocked, setBlocked] = useState<string | null>(null);

  const sessionMatches = isAuthenticated && user?.email?.toLowerCase() === preview.email.toLowerCase();

  async function handleAccept() {
    setSubmitting(true);
    setError('');
    const result = await acceptInviteAsExistingUser(token);
    setSubmitting(false);
    if (result.success && result.tenant) {
      setAccepted({ name: result.tenant.name });
    } else if (result.error?.toLowerCase().includes('already')) {
      setBlocked(result.error);
    } else {
      setError(result.error || 'Could not accept this invitation');
    }
  }

  function goLogin() {
    const redirectTo = `/invite?token=${encodeURIComponent(token)}`;
    navigate(`/login?redirect=${encodeURIComponent(redirectTo)}`);
  }

  function switchAccount() {
    logout();
    goLogin();
  }

  if (blocked) {
    return (
      <InviteCardShell>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: t.greenDim, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Users size={26} color={t.green} />
          </div>
          <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 22, color: t.fg }}>
            You're already set up
          </h1>
          <p style={{ color: t.mutedFg, fontSize: 14.5, lineHeight: 1.6 }}>{blocked}</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            style={{
              marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 10,
              border: 'none', cursor: 'pointer', background: t.green, color: isDark ? '#050C09' : '#fff',
              fontWeight: 800, fontSize: 14, fontFamily: 'Manrope, sans-serif',
            }}
          >
            Go to dashboard <ArrowRight size={15} />
          </button>
        </div>
      </InviteCardShell>
    );
  }

  if (accepted) {
    return (
      <InviteCardShell>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: t.greenDim, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle2 size={26} color={t.green} />
          </div>
          <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 22, color: t.fg }}>
            You've joined {accepted.name}
          </h1>
          <button
            onClick={() => navigate('/admin/dashboard')}
            style={{
              marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 10,
              border: 'none', cursor: 'pointer', background: t.green, color: isDark ? '#050C09' : '#fff',
              fontWeight: 800, fontSize: 14, fontFamily: 'Manrope, sans-serif',
            }}
          >
            Go to dashboard <ArrowRight size={15} />
          </button>
        </div>
      </InviteCardShell>
    );
  }

  return (
    <InviteCardShell>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: t.greenDim, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <Users size={24} color={t.green} />
        </div>
        <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 22, color: t.fg, marginBottom: 6 }}>
          Join {preview.tenantName}
        </h1>
        <p style={{ color: t.mutedFg, fontSize: 14.5, lineHeight: 1.6 }}>
          {preview.email} already has an account.{' '}
          {sessionMatches
            ? `Confirm below to join as ${roleLabel(preview.role)}.`
            : isAuthenticated
              ? `You're logged in as ${user?.email} — log out and sign in as ${preview.email} to accept.`
              : 'Log in with that account to accept this invitation.'}
        </p>
      </div>

      {error && (
        <div style={{ color: '#ef4444', fontSize: 13, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 14px' }}>
          {error}
        </div>
      )}

      {sessionMatches ? (
        <button
          onClick={handleAccept}
          disabled={submitting}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 24px',
            borderRadius: 12, border: 'none', cursor: submitting ? 'default' : 'pointer',
            background: t.green, color: isDark ? '#050C09' : '#fff', fontWeight: 800, fontSize: 15,
            fontFamily: 'Manrope, sans-serif', opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? 'Joining...' : `Join ${preview.tenantName}`} {!submitting && <ArrowRight size={16} />}
        </button>
      ) : isAuthenticated ? (
        <button
          onClick={switchAccount}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 24px',
            borderRadius: 12, border: 'none', cursor: 'pointer',
            background: t.green, color: isDark ? '#050C09' : '#fff', fontWeight: 800, fontSize: 15,
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          Log out &amp; switch account <ArrowRight size={16} />
        </button>
      ) : (
        <button
          onClick={goLogin}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 24px',
            borderRadius: 12, border: 'none', cursor: 'pointer',
            background: t.green, color: isDark ? '#050C09' : '#fff', fontWeight: 800, fontSize: 15,
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          Log in to accept <ArrowRight size={16} />
        </button>
      )}
    </InviteCardShell>
  );
}

/** New-identity branch: sets a password for this invited email — the
 *  backend hard-rejects this if the email turns out to already have an
 *  account (defense in depth; the preview should already have routed us to
 *  ExistingAccountCard in that case). */
function NewIdentityCard({ token, preview }: { token: string; preview: InvitePreview }) {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const isDark = theme === 'dark';
  const { acceptInvite, loading } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const result = await acceptInvite(token, { firstName: firstName.trim(), lastName: lastName.trim(), password });
    if (!result.success) setError(result.error || 'Could not accept this invitation');
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px', borderRadius: 10, background: t.muted,
    border: `1px solid ${t.border}`, color: t.fg, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none',
  };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 600, color: t.mutedFg, marginBottom: 6 };
  const focusHandlers = {
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => { e.currentTarget.style.borderColor = t.green; },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => { e.currentTarget.style.borderColor = t.border; },
  };

  return (
    <InviteCardShell>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: t.greenDim, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Users size={24} color={t.green} />
          </div>
          <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 22, color: t.fg, marginBottom: 6 }}>
            Join {preview.tenantName}
          </h1>
          <p style={{ color: t.mutedFg, fontSize: 14, lineHeight: 1.6 }}>
            You've been invited as <strong style={{ color: t.fg }}>{roleLabel(preview.role)}</strong>. Set a
            password for <strong style={{ color: t.fg }}>{preview.email}</strong> to get started.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="invite-grid-2">
          <div>
            <label style={labelStyle}>First name</label>
            <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" {...focusHandlers} />
          </div>
          <div>
            <label style={labelStyle}>Last name</label>
            <input style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" {...focusHandlers} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Set a password</label>
          <PasswordField
            value={password}
            onChange={setPassword}
            visible={passwordVisible}
            onToggleVisible={() => setPasswordVisible((v) => !v)}
            placeholder="At least 8 characters"
          />
        </div>

        <div>
          <label style={labelStyle}>Confirm password</label>
          <PasswordField
            value={confirmPassword}
            onChange={setConfirmPassword}
            visible={passwordVisible}
            showToggle={false}
            placeholder="Re-enter your password"
          />
        </div>

        {error && (
          <div style={{ color: '#ef4444', fontSize: 13, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 14px' }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 24px',
            borderRadius: 12, border: 'none', cursor: loading ? 'default' : 'pointer',
            background: t.green, color: isDark ? '#050C09' : '#fff', fontWeight: 800, fontSize: 15,
            fontFamily: 'Manrope, sans-serif', opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? 'Joining...' : `Join ${preview.tenantName}`} {!loading && <ArrowRight size={16} />}
        </button>
        <style>{`.invite-grid-2 { @media (max-width: 420px) { grid-template-columns: 1fr !important; } }`}</style>
      </form>
    </InviteCardShell>
  );
}

function InviteAcceptBody() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [state, setState] = useState<LoadState>('loading');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setState('invalid');
      setErrorMessage('This invitation link is missing its token.');
      return;
    }
    // `cancelled` covers both React StrictMode's dev-only double-invoke (the
    // first, aborted attempt's response must not update state) and a real
    // unmount mid-request (e.g. the visitor navigates away before it resolves).
    let cancelled = false;
    previewInvite(token)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setState('ready');
      })
      .catch((err: any) => {
        if (cancelled) return;
        setErrorMessage(
          err?.response?.data?.error?.message || 'This invitation link is invalid, expired, or has already been used.'
        );
        setState('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state === 'loading') return null;
  if (state === 'invalid' || !preview) return <InvalidInviteCard message={errorMessage} />;
  return preview.hasExistingAccount
    ? <ExistingAccountCard token={token} preview={preview} />
    : <NewIdentityCard token={token} preview={preview} />;
}

export default function InviteAccept() {
  const themeCtxValue = useThemeState('light');
  const t = THEMES[themeCtxValue.theme];

  return (
    <ThemeCtx.Provider value={themeCtxValue}>
      <div style={{ minHeight: '100vh', background: t.bg, color: t.fg, fontFamily: "'Inter', sans-serif" }}>
        <NavBar />
        <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '130px 24px 80px' }}>
          <InviteAcceptBody />
        </main>
        <Footer />
      </div>
    </ThemeCtx.Provider>
  );
}
